import React, { forwardRef } from 'react';
import { AlertCircle } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helper_text?: string;
  icon_left?: React.ReactNode;
  icon_right?: React.ReactNode;
  is_required?: boolean;
  container_class_name?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helper_text,
      icon_left,
      icon_right,
      is_required = false,
      className = '',
      container_class_name = '',
      id,
      ...props
    },
    ref
  ) => {
    const input_id = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    
    const base_input_classes = 'w-full px-4 py-2 border rounded-lg transition-smooth focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed';
    
    const state_classes = error
      ? 'border-error focus:border-error focus:ring-error focus:ring-opacity-20'
      : 'border-gray-300 focus:border-forest-green focus:ring-forest-green focus:ring-opacity-20';
    
    const icon_padding_left = icon_left ? 'pl-11' : '';
    const icon_padding_right = icon_right ? 'pr-11' : '';
    
    const input_classes = `${base_input_classes} ${state_classes} ${icon_padding_left} ${icon_padding_right} ${className}`;
    
    return (
      <div className={`w-full ${container_class_name}`}>
        {label && (
          <label 
            htmlFor={input_id} 
            className="block text-sm font-medium text-charcoal mb-1.5"
          >
            {label}
            {is_required && <span className="text-error ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {icon_left && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {icon_left}
            </div>
          )}
          
          <input
            ref={ref}
            id={input_id}
            className={input_classes}
            {...props}
          />
          
          {icon_right && !error && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {icon_right}
            </div>
          )}
          
          {error && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-error">
              <AlertCircle size={18} />
            </div>
          )}
        </div>
        
        {error && (
          <p className="mt-1.5 text-sm text-error flex items-center gap-1">
            {error}
          </p>
        )}
        
        {helper_text && !error && (
          <p className="mt-1.5 text-sm text-gray-500">
            {helper_text}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';